import { useUserLanguage } from 'apps/locale/hooks/useUserLanguage'
import { AppLanguage } from 'apps/locale/lib/constants'
import { KnowledgeBaseLink } from 'common/core/components/SupportLinks'

export enum SupportedHelpArticleLanguage {
  EN = 'en-us',
  PT = 'pt-br',
  ES = 'es',
}

const HelpArticleLanguage: Record<AppLanguage, SupportedHelpArticleLanguage> = {
  [AppLanguage.EN]: SupportedHelpArticleLanguage.EN,
  [AppLanguage.PT]: SupportedHelpArticleLanguage.PT,
  [AppLanguage.ES]: SupportedHelpArticleLanguage.ES,
}

const isAppLanguageSupported = (language: string): language is AppLanguage => {
  return Object.values<string>(AppLanguage).includes(language)
}

const isHelpArticleLanguageSupported = (
  language: AppLanguage,
  supportedLanguages: Array<`${SupportedHelpArticleLanguage}`>,
) => {
  return supportedLanguages.includes(HelpArticleLanguage[language])
}

const replaceLanguage = (url: string, language: SupportedHelpArticleLanguage) => {
  /**
   * Check if the URL already has a language code. Either "en-us", "pt-br" (i.e., xx-xx format), or "es" (i.e., xx format).
   * If it does, we need to replace the language with the current user language.
   */
  if (/\/hc\/(?:[a-z]{2}-[a-z]{2}|[a-z]{2})\//.test(url)) {
    return url.replace(/(\/hc\/)([a-z]{2}-[a-z]{2}|[a-z]{2})(?=\/)/, `$1${language}`)
  }
  /**
   * If no language is specified in the URL, we need to add the current user language to the URL.
   */
  return url.replace(/(\/hc\/)/, `$1${language}/`)
}

/**
 * Hook to replace the language in the help article URL with the current user language in Manychat.
 * @param href - The link to the help article.
 * @param supportedLanguages - The array of supported help article languages (you should ask a content manager for this).
 * @returns The link to the help article with the replaced language.
 */
export const useHelpArticleLink = (
  href: typeof KnowledgeBaseLink[keyof typeof KnowledgeBaseLink],
  supportedLanguages: Array<`${SupportedHelpArticleLanguage}`>,
) => {
  const { language } = useUserLanguage()

  const isSupportedLanguage =
    isAppLanguageSupported(language) && isHelpArticleLanguageSupported(language, supportedLanguages)

  if (isSupportedLanguage) {
    const newUrl = replaceLanguage(href, HelpArticleLanguage[language])
    return newUrl
  }

  return href
}
